import React, { useState, useCallback } from 'react'

import { Modal, Textarea, Checkbox } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  func, number, oneOfType, string,
} from 'prop-types'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'

const TRANSLATION_PREFIX = 'profile.my_products.suspension_appeal.modal'

function AppealModal({ itemId, onClose, onSubmitted }) {
  const [reason, setReason] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const { postData, isLoading } = useApiPost()

  const handleReasonChange = useCallback((e) => setReason(e.target.value), [])
  const handleAcknowledgeChange = useCallback((isChecked) => setAcknowledged(isChecked), [])

  const canSubmit = reason.trim().length > 0 && acknowledged && !isLoading

  const handleSubmit = useCallback(() => {
    if (!(reason.trim().length > 0 && acknowledged) || isLoading) return

    postData({
      path: `/api/internal/items/${itemId}/appeal`,
      data: { reason },
      onSuccess: ({ data }) => {
        if (data.success) {
          App.events.publish('app:notification:flash:success', I18n.t(`${TRANSLATION_PREFIX}.success`))
          onSubmitted(data.appeal_status)
        } else {
          App.events.publish('app:notification:flash:error', I18n.t(`${TRANSLATION_PREFIX}.error`))
        }
      },
      onError: () => App.events.publish('app:notification:flash:error', I18n.t(`${TRANSLATION_PREFIX}.error`)),
    })
  }, [acknowledged, itemId, reason, isLoading, postData, onSubmitted])

  return (
    <Modal
      header={I18n.t(`${TRANSLATION_PREFIX}.header`)}
      isOpen
      onClose={onClose}
      primaryAction={{
        buttonProps: {
          onClick: handleSubmit, type: 'primary', size: 'small', disabled: !canSubmit,
        },
        content: I18n.t(`${TRANSLATION_PREFIX}.submit`),
      }}
      secondaryAction={{
        buttonProps: { onClick: onClose, size: 'small', disabled: isLoading },
        content: I18n.t(`${TRANSLATION_PREFIX}.cancel`),
      }}
      customClass="suspension-appeal-modal"
    >
      <div className="suspension-appeal-modal__content">
        <p className="suspension-appeal-modal__intro">
          {I18n.t(`${TRANSLATION_PREFIX}.intro`)}
        </p>
        <div className="suspension-appeal-modal__policy">
          <p className="suspension-appeal-modal__policy-intro">
            {I18n.t(`${TRANSLATION_PREFIX}.policy_intro`)}
          </p>
          <ul className="suspension-appeal-modal__policy-list">
            {I18n.t(`${TRANSLATION_PREFIX}.policy_items`).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="suspension-appeal-modal__exception">
            <strong>{I18n.t(`${TRANSLATION_PREFIX}.exception_label`)}</strong>
            {' '}
            {I18n.t(`${TRANSLATION_PREFIX}.exception`)}
          </p>
        </div>
        <Textarea
          id={`appeal-reason-${itemId}`}
          label={I18n.t(`${TRANSLATION_PREFIX}.reason_field_label`)}
          placeholder={I18n.t(`${TRANSLATION_PREFIX}.reason_placeholder`)}
          rows={4}
          value={reason}
          onChange={handleReasonChange}
        />
        <Checkbox
          id={`appeal-acknowledgement-${itemId}`}
          name={`appeal-acknowledgement-${itemId}`}
          isControlled
          isChecked={acknowledged}
          onChange={handleAcknowledgeChange}
          label={I18n.t(`${TRANSLATION_PREFIX}.acknowledgement`)}
        />
        <p className="suspension-appeal-modal__footer">
          {I18n.t(`${TRANSLATION_PREFIX}.footer`)}
        </p>
        <p className="suspension-appeal-modal__note">
          <strong>{I18n.t(`${TRANSLATION_PREFIX}.note_label`)}</strong>
          {' '}
          {I18n.t(`${TRANSLATION_PREFIX}.note`)}
        </p>
      </div>
    </Modal>
  )
}

AppealModal.propTypes = {
  itemId: oneOfType([number, string]).isRequired,
  onClose: func.isRequired,
  onSubmitted: func.isRequired,
}

export default AppealModal
